import { AxiosError } from "axios";
import { AuditEntry, BulkAction } from "./types";

export const extractApiError = (err: unknown, fallback: string): string => {
  const anyErr = err as AxiosError<Record<string, unknown>>;
  const data = anyErr?.response?.data;
  if (data && typeof data === "object") {
    const detail = (data as { detail?: unknown }).detail;
    if (typeof detail === "string") return detail;
    for (const value of Object.values(data)) {
      if (Array.isArray(value) && typeof value[0] === "string")
        return value[0] as string;
      if (typeof value === "string") return value;
    }
  }
  return anyErr?.message ?? fallback;
};

export const formatDateTime = (iso: string): string =>
  new Date(iso).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

export const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });

export const skipReasonLabel = (reason: string): string => {
  switch (reason) {
    case "self":
      return "your own account";
    case "superuser":
      return "superuser";
    case "not_found":
      return "not found";
    case "no_change":
      return "already in target state";
    default:
      return reason;
  }
};

export const actionLabel = (action: AuditEntry["action"]): string => {
  switch (action) {
    case "role_changed":
      return "Role changed";
    case "activated":
      return "Activated";
    case "deactivated":
      return "Deactivated";
    case "password_reset":
      return "Password reset";
    case "migration_created":
      return "Migration created";
    case "migration_canceled":
      return "Migration canceled";
    case "migration_retried":
      return "Migration retried";
    case "migration_finished":
      return "Migration finished";
    case "data_migrated":
      return "Data migrated";
    case "data_rolled_back":
      return "Data rolled back";
    case "data_edited":
      return "Data edited";
    case "data_deleted":
      return "Data deleted";
    default:
      return action;
  }
};

export const actionBadgeClass = (action: AuditEntry["action"]): string => {
  switch (action) {
    case "role_changed":
      return "bg-indigo-100 text-indigo-700";
    case "activated":
      return "bg-green-100 text-green-700";
    case "deactivated":
      return "bg-gray-200 text-gray-700";
    case "password_reset":
      return "bg-orange-100 text-orange-700";
    case "migration_created":
      return "bg-blue-100 text-blue-700";
    case "migration_canceled":
      return "bg-yellow-100 text-yellow-800";
    case "migration_retried":
      return "bg-indigo-100 text-indigo-700";
    case "migration_finished":
      return "bg-green-100 text-green-700";
    case "data_migrated":
      return "bg-blue-100 text-blue-700";
    case "data_rolled_back":
      return "bg-yellow-100 text-yellow-800";
    case "data_edited":
      return "bg-purple-100 text-purple-700";
    case "data_deleted":
      return "bg-red-100 text-red-700";
    default:
      return "bg-gray-100 text-gray-700";
  }
};

export const describeChanges = (entry: AuditEntry): string => {
  const c = entry.changes as Record<
    string,
    { before?: unknown; after?: unknown } | number | string | null | undefined
  >;
  if (entry.action === "role_changed" && c.role && typeof c.role === "object") {
    const role = c.role as { before?: unknown; after?: unknown };
    return `${role.before} → ${role.after}`;
  }
  if (entry.action === "activated" || entry.action === "deactivated") {
    return entry.action === "activated"
      ? "inactive → active"
      : "active → inactive";
  }
  if (entry.action === "password_reset") return "temporary password issued";
  if (entry.action === "migration_created") {
    const jobId = typeof c.job_id === "number" ? c.job_id : "?";
    const total = typeof c.total === "number" ? c.total : "?";
    return `job #${jobId} · ${total} rows`;
  }
  if (entry.action === "migration_finished") {
    const jobId = typeof c.job_id === "number" ? c.job_id : "?";
    const inserted = typeof c.inserted === "number" ? c.inserted : "?";
    const skipped = typeof c.skipped === "number" ? c.skipped : "?";
    return `job #${jobId} · inserted ${inserted} skipped ${skipped}`;
  }
  if (entry.action === "migration_canceled") {
    return `job #${typeof c.job_id === "number" ? c.job_id : "?"}`;
  }
  if (entry.action === "migration_retried") {
    const from = typeof c.from_job_id === "number" ? c.from_job_id : "?";
    const to = typeof c.to_job_id === "number" ? c.to_job_id : "?";
    return `retry #${from} → #${to}`;
  }
  if (entry.action === "data_migrated" || entry.action === "data_rolled_back") {
    const jobId = typeof c.job_id === "number" ? c.job_id : "?";
    const count = typeof c.count === "number" ? c.count : "?";
    const model = typeof c.source_model === "string" ? c.source_model : "data";
    return `job #${jobId} · ${count} ${model} rows`;
  }
  if (entry.action === "data_edited") {
    const index = typeof c.index === "number" ? c.index : "?";
    const fields = Array.isArray(c.fields) ? c.fields.join(", ") : "fields";
    return `row #${index} · edited: ${fields}`;
  }
  if (entry.action === "data_deleted") {
    const count = typeof c.count === "number" ? c.count : "?";
    return `deleted ${count} row(s)`;
  }
  return "";
};

export const bulkActionLabel = (action: BulkAction): string =>
  ({
    promote_admin: "Promote to admin",
    demote_viewer: "Demote to viewer",
    activate: "Activate",
    deactivate: "Deactivate",
  })[action];

export const getInitial = (email: string): string =>
  email.trim().charAt(0).toUpperCase() || "?";
